import 'cross-fetch/polyfill'
import { normalize, schema as normSchema } from 'normalizr'
import type { Dispatch, AnyAction } from 'redux'

// Actions
import { actions as authActions, getAuthToken } from 'modules/auth/actions'

// Types
import type { CallAPI } from 'types'

// Utils
import { DEFAULT_FETCH_HEADERS, API_ROOT } from '@constants'

const getNextPageNumber = (json: Record<string, unknown>, string: string) => {
  const url = json[string] as string
  if (!url) {
    return null
  }

  if (!url.includes('page=')) {
    return null
  }
  const nextLink = url.split('page=')[1].split('&')[0]
  if (!nextLink) {
    return null
  }
  return +nextLink
}

// URLs that never should include an auth token
const authTokenBlackList = ['/api/auth/login', '/api/paymentmethods/']

export const getHeader = (authToken: string, endpoint: string) =>
  authToken && authTokenBlackList.indexOf(endpoint) === -1 ? { Authorization: `Token ${authToken}` } : {}

const callApi = (
  relativeEndpoint: string,
  schema: normSchema.Entity | normSchema.Entity[] | null | undefined,
  body: Record<string, unknown> | undefined,
  method = 'post',
  authToken: string,
) => {
  const endpoint = API_ROOT + relativeEndpoint

  const tupleToSchemaOrReject = ({ json, response }: { json: Record<string, unknown>; response: Record<string, unknown> }) => {
    if (response.ok) {
      if (!schema) {
        return json
      }
      const metadata = {
        next: getNextPageNumber(json, 'next'),
        previous: getNextPageNumber(json, 'previous'),
        count: json.count || json.length || 0,
      }

      let jsn = json.hasOwnProperty('results') ? json.results : json
      jsn = jsn.items && jsn.meta && json.meta.total_count ? jsn.items : jsn
      return { ...normalize(jsn, schema), ...metadata }
    }

    /* eslint-disable */
    return Promise.reject({ json, response })
    /* eslint-enable */
  }

  const header = getHeader(authToken, endpoint)
  const fetchOptions = {
    method,
    headers: {
      ...header,
      ...DEFAULT_FETCH_HEADERS,
    },
    credentials: 'include',
    body: JSON.stringify(body),
  }

  const fetchPromise = fetch(endpoint, fetchOptions)

  if (method.toLowerCase() === 'delete') {
    return fetchPromise.then(response => ({ response, json: '' }))
  }

  return fetchPromise
    .then(response =>
      response
        .json()
        .then(json => ({ json, response }))
        .catch(() => ({ json: {}, response })),
    )
    .then(tupleToSchemaOrReject)
}

export default () => (next: Dispatch<AnyAction>) => (action: AnyAction) => {
  const callAPI = action.CALL_API as CallAPI
  if (!callAPI) {
    return next(action)
  }

  const { endpoint, schema, type, data, method, requestKey, actionParams, merge } = callAPI

  const authToken = getAuthToken()

  const actionWith = (actionData: Record<string, unknown>) => {
    const finalAction = { ...action, ...actionData }
    delete finalAction.CALL_API
    return finalAction
  }

  next(actionWith({ type: `${type}_START`, ...actionParams, requestKey, merge }))

  return callApi(endpoint, schema, data, method, authToken).then(
    response => {
      if (response && response.url && response.url.startsWith('ws')) {
        return new Promise(resolve => {
          const socket = new window.WebSocket(response.url)
          socket.addEventListener('message', function _listener({ data: socketData }) {
            resolve(
              next(
                actionWith({
                  data: {
                    response: {
                      ...response,
                      url: socketData,
                    },
                    ...actionParams,
                  },
                  requestKey,
                  type: `${type}_DONE`,
                  merge,
                }),
              ),
            )
            socket.removeEventListener('message', _listener)
          })
        })
      } else {
        return next(
          actionWith({
            data: { response, ...actionParams },
            requestKey,
            type: `${type}_DONE`,
            merge,
          }),
        )
      }
    },
    error => {
      if (error && error.response && error.response.status === 401) {
        if (!endpoint.startsWith('/api/auth')) {
          return next(authActions.logout())
        }
      }
      return next(
        actionWith({
          type: `${type}_ERROR`,
          requestKey,
          merge,
          data: {
            error: (error && error.json) || 'Something bad happened',
            ...actionParams,
          },
        }),
      )
    },
  )
}
